import { InlineLoader, Size, breakpoints } from "@asurion-core/mx-asurion-ui-react";
import { useDebounce, useNavigateWithParams } from "@ubif/components";
import { Client, CustomerType } from "@ubif/types";
import {
  SearchCustomersResponse,
  SearchWorkordersResponse,
  checkScanId,
  setShowSearchResults,
  useGlobalDispatch
} from "@ubif/util-store";
import { useEffect, useMemo, useState, useTransition } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { CustomerSearchResults } from "./CustomerSearchResults";
import { WorkorderSearchResults } from "./WorkorderSearchResults";
import { CustomerSearchNoResults } from "./elements/CustomerSearchNoResults";
import { StyledLoader } from "./elements/FlexContainers";

const NUM_RECORDS_TO_SHOW = 12;

export type GlobalSearchResultsProps = {
  navClosed: boolean;
  moreClosed?: boolean;
  openSearchResults: boolean;
  searchCriteriaRaw: string;
  searchedCustomersData?: SearchCustomersResponse;
  searchedWorkorderData?: SearchWorkordersResponse["workOrders"];
  isSearchInProgress: boolean;
  isSearchSuccessOrError: boolean;
};

export const GlobalSearchResults = ({
  navClosed,
  openSearchResults,
  searchCriteriaRaw,
  searchedCustomersData = [],
  searchedWorkorderData = [],
  isSearchInProgress,
  isSearchSuccessOrError
}: GlobalSearchResultsProps) => {
  const dispatch = useGlobalDispatch();
  const location = useLocation();
  const navigateWithParams = useNavigateWithParams();
  const navigate = useNavigate();

  const [showMoreCustomers, setShowMoreCustomers] = useState<boolean>(false);
  const [isShowMoreCustomersPending, startCustomersTransition] = useTransition();
  const debouncedSearchCriteria = useDebounce(searchCriteriaRaw.trim(), 500);

  const [showMoreWorkorders, setShowMoreWorkorders] = useState<boolean>(false);
  const [isShowMoreWorkordersPending, startWorkordersTransition] = useTransition();

  // when path is changing hide overlay
  useEffect(() => {
    dispatch(setShowSearchResults(false));
  }, [location.pathname]);

  useEffect(() => {
    if (openSearchResults && searchedCustomersData.length === 1) {
      handleCustomerSelection(searchedCustomersData[0].id, searchedCustomersData[0]?.clientId);
    }
  }, [openSearchResults, searchedCustomersData]);

  useEffect(() => {
    if ((!searchedCustomersData || searchedCustomersData?.length === 0) && showMoreCustomers) {
      setShowMoreCustomers(false);
    }
    if (
      searchedCustomersData &&
      searchedCustomersData.length !== 0 &&
      searchedCustomersData.length <= NUM_RECORDS_TO_SHOW
    ) {
      setShowMoreCustomers(true);
    }
  }, [searchedCustomersData, showMoreCustomers]);

  const customersCount = useMemo<number>(() => {
    return searchedCustomersData ? searchedCustomersData.length : 0;
  }, [searchedCustomersData]);

  const customersToDisplay = useMemo<SearchCustomersResponse>(() => {
    if (searchedCustomersData?.length) {
      if (searchedCustomersData.length > NUM_RECORDS_TO_SHOW && showMoreCustomers) {
        return searchedCustomersData;
      } else {
        return searchedCustomersData.slice(0, NUM_RECORDS_TO_SHOW);
      }
    }
    return [];
  }, [searchedCustomersData, showMoreCustomers]);

  const handleCustomerSelection = (customerId: string, clientId: Client = Client.UBIF, customerTypeId?: number) => {
    dispatch(setShowSearchResults(false));
    if (customerTypeId === CustomerType.B2B.valueOf()) {
      navigate(`/admin/business-clients/${customerId}`);
      return;
    } else {
      navigateWithParams(`/customer/${customerId}`, {
        searchTerm: debouncedSearchCriteria,
        showBreadcrumb: (searchedCustomersData?.length > 1).toString(),
        clientId: clientId.toString()
      });
    }
  };

  const handleCustomerShowMoreClick = () => {
    startCustomersTransition(() => {
      setShowMoreCustomers(true);
    });
  };

  useEffect(() => {
    if (openSearchResults && searchedWorkorderData.length === 1) {
      handleWorkorderSelection(searchedWorkorderData[0].workorderId);
    }
  }, [openSearchResults, searchedWorkorderData]);

  useEffect(() => {
    if ((!searchedWorkorderData || searchedWorkorderData?.length === 0) && showMoreWorkorders) {
      setShowMoreWorkorders(false);
    }
    if (
      searchedWorkorderData &&
      searchedWorkorderData.length !== 0 &&
      searchedWorkorderData.length <= NUM_RECORDS_TO_SHOW
    ) {
      setShowMoreWorkorders(true);
    }
  }, [searchedWorkorderData, showMoreWorkorders]);

  const workordersCount = useMemo<number>(() => {
    return searchedWorkorderData ? searchedWorkorderData.length : 0;
  }, [searchedWorkorderData]);

  const workordersToDisplay = useMemo<SearchWorkordersResponse["workOrders"]>(() => {
    if (searchedWorkorderData?.length) {
      if (searchedWorkorderData.length > NUM_RECORDS_TO_SHOW && showMoreWorkorders) {
        return searchedWorkorderData;
      } else {
        return searchedWorkorderData.slice(0, NUM_RECORDS_TO_SHOW);
      }
    }
    return [];
  }, [searchedWorkorderData, showMoreWorkorders]);

  const handleWorkorderSelection = (workorderId: number) => {
    dispatch(setShowSearchResults(false));
    navigateWithParams(`/repair/workorder/${workorderId}`, {});
  };

  const handleWorkorderShowMoreClick = () => {
    startWorkordersTransition(() => {
      setShowMoreWorkorders(true);
    });
  };

  return (
    openSearchResults && (
      <SearchResultsContainer navClosed={navClosed}>
        {isSearchInProgress ? (
          <StyledLoader>
            <InlineLoader width="3rem" title="Searching" />
          </StyledLoader>
        ) : (
          <>
            {customersToDisplay.length > 0 && (
              <CustomerSearchResults
                customersToDisplay={customersToDisplay}
                searchedCustomersData={searchedCustomersData}
                customersCount={customersCount}
                customerSelectionClick={handleCustomerSelection}
                debouncedSearchCriteria={debouncedSearchCriteria}
                isSearchSuccessOrError={isSearchSuccessOrError}
                moreClick={handleCustomerShowMoreClick}
                showMoreCustomers={showMoreCustomers}
                isShowMoreCustomersPending={isShowMoreCustomersPending}
              />
            )}
            {customersToDisplay.length === 0 &&
              debouncedSearchCriteria &&
              debouncedSearchCriteria.length > 3 &&
              !checkScanId("PE", debouncedSearchCriteria) && (
                <CustomerSearchNoResults searchText={debouncedSearchCriteria} />
              )}
            {/*
             * The default behaving of customer search if customer is having multiple workorder.
             * Show them on by displaying cards.
             * Now, we are using same customer chip for price quote. we don't to show the workorder.
             * To differentiate between if it is Price estimate search or customer.
             * using !checkScanId("PE",searchCriteriaRaw) check if it price quote or customer search
             */}
            {workordersToDisplay.length && !checkScanId("PE", searchCriteriaRaw) ? (
              <WorkorderSearchResults
                workordersToDisplay={workordersToDisplay}
                searchedWorkorderData={searchedWorkorderData}
                workordersCount={workordersCount}
                workorderSelectionClick={handleWorkorderSelection}
                moreClick={handleWorkorderShowMoreClick}
                debouncedSearchCriteria={debouncedSearchCriteria}
                isSearchSuccessOrError={isSearchSuccessOrError}
                showMoreWorkorders={showMoreWorkorders}
                isShowMoreWorkordersPending={isShowMoreWorkordersPending}
              />
            ) : null}
          </>
        )}
      </SearchResultsContainer>
    )
  );
};

const SearchResultsContainer = styled.div<{ navClosed: boolean }>`
  margin-left: 4rem;
  overflow-y: auto;
  height: calc(100vh - var(--nav-height));
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  background: var(--aui-elevated-level-03);
  position: absolute;
  z-index: 1000;
  width: calc(100vw - 4rem);

  ${breakpoints.lg} {
    ${(props) =>
      !props.navClosed
        ? "margin-left: 12rem; width: calc(100vw - 12rem);"
        : "margin-left: 4rem; width: calc(100vw - 4rem);"};
  }

  ${breakpoints.xxl} {
    ${(props) =>
      !props.navClosed
        ? `margin-left: 12rem; width: calc(${Size.XXL}px - 12rem);`
        : `margin-left: 4rem; width: calc(${Size.XXL}px - 4rem);`};
  }

  .page-heading-pb {
    padding-bottom: 1.5rem;
    font-size: 1.75rem;
  }
`;
